/**
 * PATH: packages/ui/src/kanban/components/KanbanCardQuickActionsPopover.tsx
 * FILE_ID: UI-KANBAN-CARD-QUICKACTIONS-POPOVER-001
 * PURPOSE: Portaled card-action picker shell, anchored beside its trigger icon.
 * WHY:
 *   FEAT-2026-06-20-KANBAN-CARD-ACTION-RAIL — rail buttons live inside an
 *   overflow-hidden grow cell, so pickers must portal to <body>.
 *   BUG-2026-07-29-KANBAN-CARD-ACTION-POPOVER-OVER-RAIL — bottom-anchored
 *   + viewport clamp shoved tall Due/Assign menus over the action icons.
 *   Side placement (placeBesideTrigger) keeps the clicked icon visible.
 * FLOW:
 *   trigger rect → measure menu → placeBesideTrigger → fixed portal
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { useDismissOnOutsideInteraction } from '../hooks/useDismissOnOutsideInteraction'
import { Z_LAYER } from '../zLayers'
import {
  placeBesideTrigger,
  type TriggerRect,
} from './placeBesideTrigger'

export interface KanbanCardQuickActionsPopoverProps {
  /** Trigger icon's viewport rect at open time. */
  trigger: TriggerRect
  /** Accessible label for the popover container. */
  label: string
  onClose: () => void
  children: ReactNode
  /** Fixed popover width (Tailwind class). Defaults to a compact picker. */
  widthClassName?: string
  /** Hover-bridge hooks shared with the rail. */
  onHoverIn?: () => void
  onHoverOut?: () => void
}

/**
 * Portaled picker shell. Pure chrome: the caller supplies the option rows as
 * children; this owns positioning + dismissal so every picker behaves the same.
 */
export function KanbanCardQuickActionsPopover({
  trigger,
  label,
  onClose,
  children,
  widthClassName = 'w-56',
  onHoverIn,
  onHoverOut,
}: KanbanCardQuickActionsPopoverProps) {
  const popRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState(() =>
    placeBesideTrigger(trigger, { width: 224, height: 40 }),
  )

  useDismissOnOutsideInteraction(popRef, onClose)

  useEffect(() => {
    const el = popRef.current
    if (!el) return

    const recompute = () => {
      const node = popRef.current
      if (!node) return
      const rect = node.getBoundingClientRect()
      const next = placeBesideTrigger(trigger, {
        width: rect.width || 224,
        height: rect.height || 40,
      })
      setPos((prev) =>
        prev.left === next.left && prev.top === next.top && prev.side === next.side
          ? prev
          : next,
      )
    }

    recompute()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => recompute())
    ro.observe(el)
    window.addEventListener('resize', recompute)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', recompute)
    }
  }, [trigger.left, trigger.top, trigger.right, trigger.bottom, trigger.width, trigger.height])

  if (typeof document === 'undefined') return null

  // Hover apron faces the trigger so the pointer can travel from icon → menu
  // without slipping into dead space and arming the close timer.
  const apronClass =
    pos.side === 'right'
      ? 'before:absolute before:-left-3 before:top-0 before:h-full before:w-3 before:content-[""]'
      : 'before:absolute before:-right-3 before:top-0 before:h-full before:w-3 before:content-[""]'

  return createPortal(
    <div
      ref={popRef}
      role="menu"
      aria-label={label}
      data-kanban-card-quick-popover=""
      data-placement-side={pos.side}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: Z_LAYER.popover }}
      className={[widthClassName, 'relative', apronClass].join(' ')}
    >
      <div
        className={[
          'max-h-[60vh] overflow-y-auto rounded-lg border border-neutral-200 bg-white py-1 text-sm shadow-xl',
          'dark:border-zinc-700 dark:bg-zinc-900',
        ].join(' ')}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
